/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { PreviousPeriod } from "~/components/DateRangeSelector/types";
import { dateFormat } from "~/constants";
import { useBillingPeriod } from "~/services/customers";
import { formatShortDate } from "~/tools";

type DateRange = {
  from: string;
  to: string;
};

// This is a workaround for a problem with date input from keyboard, we cannot dynamically parse incomplete input date because it will be constantly reset to the full year
// E.g. User starts typing "12/12/1", the incomplete 0001-12-12 date will be parsed into `1901-12-12` ISO format, making it impossible to enter desired year
export function useDateValues(range?: DateRange) {
  const [startDate, setStartDate] = useState(formatShortDate(range?.from));
  const [endDate, setEndDate] = useState(formatShortDate(range?.to));
  const { isPreviousBillingPeriod } = useBillingPeriod();

  const [previousPeriodSelected, setPreviousPeriodSelected] = useState<
    PreviousPeriod | undefined
  >(undefined);

  const handlePreviousPeriodClick = (value: PreviousPeriod) => {
    setPreviousPeriodSelected(value);
    const dates = getPreviousPeriodStartEndDates(
      value,
      isPreviousBillingPeriod
    );

    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
  };

  useEffect(() => {
    setStartDate(formatShortDate(range?.from));
  }, [range?.from]);

  useEffect(() => {
    setEndDate(formatShortDate(range?.to));
  }, [range?.to]);

  return {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    previousPeriodSelected,
    setPreviousPeriodSelected,
    handlePrevPeriodClick: handlePreviousPeriodClick,
  };
}

function getPreviousPeriodStartEndDates(
  value: PreviousPeriod,
  isPreviousBillingPeriod: boolean
) {
  const today = isPreviousBillingPeriod
    ? dayjs().subtract(1, "month").endOf("month").format(dateFormat.shortDate)
    : dayjs().format(dateFormat.shortDate);
  switch (value) {
    case PreviousPeriod.PREV_3_MONTHS:
      return {
        startDate: getAdjustedStartDate(3, "month"),
        endDate: today,
      };
    case PreviousPeriod.PREV_6_MONTHS:
      return {
        startDate: getAdjustedStartDate(6, "month"),
        endDate: today,
      };
    case PreviousPeriod.PREV_12_MONTHS:
      return {
        startDate: getAdjustedStartDate(12, "month"),
        endDate: today,
      };
    case PreviousPeriod.PREV_YEAR:
      return {
        startDate: getAdjustedStartDate(1, "year"),
        endDate: getAdjustedEndDate(1, "year"),
      };
    case PreviousPeriod.PREV_2_YEAR:
      return {
        startDate: getAdjustedStartDate(2, "year"),
        endDate: getAdjustedEndDate(2, "year"),
      };
    default:
      return {
        startDate: getAdjustedStartDate(0, "year"),
        endDate: today,
      };
  }
}

function getAdjustedStartDate(adjustBy: number, period: "month" | "year") {
  const date = getDate(adjustBy, period);
  return date.startOf(period).format(dateFormat.shortDate);
}

function getAdjustedEndDate(adjustBy: number, period: "month" | "year") {
  const date = getDate(adjustBy, period);
  return date.endOf(period).format(dateFormat.shortDate);
}

function getDate(adjustBy: number, period: "month" | "year") {
  return dayjs().subtract(adjustBy, period);
}
